import React, { useEffect, useState } from 'react';
import { Form, Typography, Checkbox, Button, Spin } from 'antd';
import {
  InfoCircleOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { FormInstance } from 'antd/es/form';
// Api
import { useLazyQuery } from '@apollo/client';
import { SEARCH_CUSTOM_STORES } from 'api/campaignV2/queries';
import {
  SearchCustomStores,
  SearchCustomStoresVariables,
} from 'api/campaignV2/types/SearchCustomStores';
// Helpers
import {
  buildCustomCriteriaInput,
  CustomCampaignFormValues,
} from 'helpers/customCampaign';
// Components
import TalentList from 'pages/CampaignDetails/CampaignDetails/components/TalentList/TalentList';
import CustomCampaignTalentFilters from '../CustomCampaignTalentFilters/CustomCampaignTalentFilters';
import CustomCampaignSocialFilters from '../CustomCampaignSocialFilters/CustomCampaignSocialFilters';
import CustomCampaignAudienceFilters from '../CustomCampaignAudienceFilters/CustomCampaignAudienceFilters';
// Utils
import { PlatformCriteriaItem } from 'helpers/customCampaignConstants';
// Style
import styles from './TalentAudienceSection.module.scss';

const { Text } = Typography;

const PAGE_SIZE = 8;

type TalentAudienceSectionProps = {
  form: FormInstance;
  isPrivate: boolean;
  onVisibilityChange: (value: boolean) => void;
  platformCriteria: PlatformCriteriaItem[];
  setPlatformCriteria: React.Dispatch<
    React.SetStateAction<PlatformCriteriaItem[]>
  >;
  campaignId: string;
  ownerId?: string;
  canEditPremiumFilters: boolean;
};

const TalentAudienceSection = ({
  form,
  isPrivate,
  onVisibilityChange,
  platformCriteria,
  setPlatformCriteria,
  ownerId,
  campaignId,
  canEditPremiumFilters,
}: TalentAudienceSectionProps): JSX.Element => {
  const [page, setPage] = useState(0);
  const [lastResult, setLastResult] = useState<
    SearchCustomStores['searchCustomStores'] | null
  >(null);

  const [searchCustomStores, { data: storeData, loading: loadingStoreData }] =
    useLazyQuery<SearchCustomStores, SearchCustomStoresVariables>(
      SEARCH_CUSTOM_STORES,
      { fetchPolicy: 'network-only' }
    );

  const runTalentSearch = (targetPage = 0) => {
    const values = {
      ...(form.getFieldsValue(true) as CustomCampaignFormValues),
      locations: form.getFieldValue('locations') || [],
      hometownLocations: form.getFieldValue('hometownLocations') || [],
      excludedLocations: form.getFieldValue('excludedLocations') || [],
      audienceLocations: form.getFieldValue('audienceLocations') || [],
    };
    const criteria = buildCustomCriteriaInput({ values, platformCriteria });

    setPage(targetPage);
    searchCustomStores({
      variables: {
        input: {
          limit: PAGE_SIZE,
          campaignId,
          offset: targetPage * PAGE_SIZE,
          ...(ownerId && { ownerId }),
          criteria,
        },
      },
    });
  };

  // Load the talent list when the section becomes public, and reset the
  // pagination state when it goes private so stale results don't reappear.
  useEffect(() => {
    if (!isPrivate) {
      runTalentSearch(0);
    } else {
      setPage(0);
      setLastResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate]);

  // Keep the last resolved result so the list & pagination stay visible during
  // refetches — network-only clears `storeData` while a new page is in flight.
  const result = storeData?.searchCustomStores;
  useEffect(() => {
    if (result) {
      setLastResult(result);
    }
  }, [result]);

  const displayed = result ?? lastResult;
  const talentsData = displayed?.entities;
  const totalTalents = Number(displayed?.total ?? 0);
  const totalFollowers = Number(displayed?.summary?.followers ?? 0);
  const hasMore = (talentsData?.length ?? 0) < totalTalents;
  const hasTalents = !!talentsData?.length;
  const showTalentList = hasTalents;

  const totalPages = Math.ceil(totalTalents / PAGE_SIZE);
  const showPagination = hasTalents && totalPages > 1;
  const canGoPrev = page > 0 && !loadingStoreData;
  const canGoNext = page < totalPages - 1 && !loadingStoreData;

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Talent &amp; Audience</h3>
      <div className={styles.subSection}>
        <h4 className={styles.subSectionTitle}>Campaign Visibility</h4>
        <div className={styles.visibilityCard}>
          <Form.Item name="isPrivate" valuePropName="checked" noStyle>
            <Checkbox
              checked={isPrivate}
              onChange={(e) => onVisibilityChange(e.target.checked)}
            >
              Make this campaign private and invite-only
            </Checkbox>
          </Form.Item>
          <Text type="secondary" className={styles.visibilityHint}>
            {isPrivate
              ? 'Private campaigns skip athlete search. Talent is added through invite links.'
              : 'Public campaigns match talent using the filters below.'}
          </Text>
        </div>
      </div>

      {!isPrivate && (
        <div className={styles.filters}>
          <CustomCampaignTalentFilters form={form} />

          <CustomCampaignSocialFilters
            platformCriteria={platformCriteria}
            setPlatformCriteria={setPlatformCriteria}
          />

          <CustomCampaignAudienceFilters
            form={form}
            canEditPremiumFilters={canEditPremiumFilters}
          />

          <div className={styles.filterGroup}>
            <div className={styles.refreshButtonWrapper}>
              <h4 className={styles.matchingTalentTitle}>Matching Talent</h4>
              <Button
                type="primary"
                htmlType="button"
                loading={loadingStoreData}
                onClick={() => runTalentSearch(0)}
              >
                Refresh List
              </Button>
            </div>

            <div className={styles.infoRow}>
              <div className={styles.matchingTalentInfo}>
                <InfoCircleOutlined className={styles.matchingTalentInfoIcon} />
                <span>
                  Talent won&rsquo;t appear until <strong>Sports</strong>,{' '}
                  <strong>Location</strong>, and{' '}
                  <strong>Price Per Talent</strong> are set (
                  <strong>Talent Type</strong> is pre-selected). Drafts missing
                  any of these show no matches.
                </span>
              </div>

              {showPagination && (
                <div className={styles.pagination}>
                  <Button
                    type="text"
                    htmlType="button"
                    icon={<LeftOutlined />}
                    disabled={!canGoPrev}
                    onClick={() => runTalentSearch(page - 1)}
                    aria-label="Previous page"
                  />
                  <span className={styles.pageInfo}>
                    {page + 1} / {totalPages}
                  </span>
                  <Button
                    type="text"
                    htmlType="button"
                    icon={<RightOutlined />}
                    disabled={!canGoNext}
                    onClick={() => runTalentSearch(page + 1)}
                    aria-label="Next page"
                  />
                </div>
              )}
            </div>

            {showTalentList ? (
              <Spin spinning={loadingStoreData}>
                <TalentList
                  talents={talentsData || []}
                  hasMore={hasMore}
                  totalTalents={totalTalents}
                  totalFollowers={totalFollowers}
                />
              </Spin>
            ) : loadingStoreData ? (
              <div className={styles.loader}>
                <Spin />
              </div>
            ) : talentsData !== undefined ? (
              <div className={styles.noTalents}>
                No talent matched the current filters.
              </div>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
};

export default TalentAudienceSection;
